import React, { useEffect, useRef, useState } from 'react';

import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { loadGoogleMapsAPI } from '../../../utils/googleMapsLoader';

const AddressAutocomplete = ({ 
  value, 
  onChange, 
  onValidationChange,
  index,
  error,
  required = true,
  placeholder = "Enter address or place name",
  onUseCurrentLocation
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [isValidating, setIsValidating] = useState(false);
  const [validationStatus, setValidationStatus] = useState(null); // 'valid', 'invalid', null
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [touched, setTouched] = useState(false);
  const validationTimeoutRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    // Use centralized loader to prevent multiple script loads
    loadGoogleMapsAPI()?.then(() => {
        if (isMounted && inputRef?.current) {
          initializeAutocomplete();
        }
      })?.catch((error) => {
        console.error('Failed to load Google Maps:', error);
      });

    function initializeAutocomplete() {
      if (inputRef?.current && window.google?.maps?.places?.Autocomplete) {
        const autocomplete = new window.google.maps.places.Autocomplete(
          inputRef.current,
          {
            types: ['geocode', 'establishment'],
            fields: ['formatted_address', 'geometry', 'name', 'place_id']
          }
        );

        autocomplete?.addListener('place_changed', () => {
          const place = autocomplete?.getPlace();
          
          if (place?.geometry) {
            const address = place?.formatted_address || place?.name;
            setSelectedPlace(place);
            onChange(index, address);
            setValidationStatus('valid');
            setShowConfirmation(true);
            onValidationChange?.(index, true);
            
            // Hide confirmation after 3 seconds
            setTimeout(() => {
              setShowConfirmation(false);
            }, 3000);
          }
        });

        autocompleteRef.current = autocomplete;
      }
    }

    return () => {
      isMounted = false;
      if (autocompleteRef?.current) {
        window.google?.maps?.event?.clearInstanceListeners(autocompleteRef?.current);
      }
    };
  }, []);

  // Real-time validation as user types
  useEffect(() => {
    if (!value || !touched) {
      setValidationStatus(null);
      return;
    }

    // Clear previous timeout
    if (validationTimeoutRef?.current) {
      clearTimeout(validationTimeoutRef?.current);
    }

    // Debounce validation by 800ms
    validationTimeoutRef.current = setTimeout(() => {
      validateAddress(value);
    }, 800);

    return () => {
      if (validationTimeoutRef?.current) {
        clearTimeout(validationTimeoutRef?.current);
      }
    };
  }, [value, touched]);

  const validateAddress = async (address) => {
    if (!address?.trim()) {
      setValidationStatus(null);
      onValidationChange?.(index, false);
      return;
    }

    const apiKey = import.meta.env?.VITE_GOOGLE_MAPS_API_KEY;
    
    if (!apiKey || apiKey === 'your-google-maps-api-key-here') {
      setValidationStatus(null);
      return;
    }

    setIsValidating(true);

    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${apiKey}`
      );
      const data = await response?.json();

      if (data?.status === 'OK' && data?.results?.[0]) {
        const result = data?.results?.[0];
        
        // Check if it's a partial match (incomplete address)
        if (result?.partial_match) {
          setValidationStatus('invalid');
          onValidationChange?.(index, false);
        } else {
          setValidationStatus('valid');
          onValidationChange?.(index, true);
        }
      } else {
        setValidationStatus('invalid');
        onValidationChange?.(index, false);
      }
    } catch (error) {
      console.error('Validation error:', error);
      setValidationStatus('invalid');
      onValidationChange?.(index, false);
    } finally {
      setIsValidating(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e?.target?.value;
    onChange(index, newValue);
    setShowConfirmation(false);
    setSelectedPlace(null);
    
    // Reset validation status when user types after selecting
    if (validationStatus === 'valid') {
      setValidationStatus(null);
    }
  };

  const handleBlur = () => {
    setTouched(true);
  };

  const getValidationIcon = () => {
    if (isValidating) {
      return (
        <div className="absolute right-12 top-9 flex items-center">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent"></div>
        </div>
      );
    }

    if (validationStatus === 'valid') {
      return (
        <div className="absolute right-12 top-9 flex items-center">
          <Icon name="CheckCircle" size={18} className="text-success" />
        </div>
      );
    }

    if (validationStatus === 'invalid' && touched && value) {
      return (
        <div className="absolute right-12 top-9 flex items-center">
          <Icon name="AlertCircle" size={18} className="text-destructive" />
        </div>
      );
    }

    return null;
  };

  const getValidationMessage = () => {
    if (!touched || !value) return error;

    if (validationStatus === 'invalid') {
      return 'Address not found or incomplete. Please select a valid address from the dropdown suggestions.';
    }

    return error;
  };

  const getInputClassName = () => {
    let className = 'pr-24';
    
    if (validationStatus === 'valid' && touched && value) {
      className += ' border-success focus:border-success focus:ring-success';
    } else if (validationStatus === 'invalid' && touched && value) {
      className += ' border-destructive focus:border-destructive focus:ring-destructive';
    }
    
    return className;
  };

  return (
    <div className="relative">
      <div className="relative">
        <Input
          ref={inputRef}
          label="Starting Location"
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={handleInputChange}
          onBlur={handleBlur}
          error={getValidationMessage()}
          required={required}
          className={getInputClassName()}
        />
        {getValidationIcon()}
        <button
          onClick={() => onUseCurrentLocation(index)}
          className="absolute right-3 top-9 p-2 rounded-lg text-primary hover:bg-primary/10 transition-smooth active-press focus:outline-none focus:ring-2 focus:ring-ring"
          aria-label="Use current location"
          title="Use my current location"
          type="button"
        >
          <Icon name="MapPin" size={18} />
        </button>
      </div>
      
      {/* Success confirmation */}
      {showConfirmation && selectedPlace && (
        <div className="mt-2 flex items-start gap-2 p-3 bg-success/10 border border-success/20 rounded-lg animate-in fade-in slide-in-from-top-2 duration-300">
          <Icon name="CheckCircle" size={18} className="text-success flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-success">Address confirmed</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {selectedPlace?.formatted_address || selectedPlace?.name}
            </p>
          </div>
        </div>
      )}
      
      {/* Validation in progress */}
      {isValidating && touched && value && (
        <div className="mt-2 flex items-start gap-2 p-3 bg-muted/50 border border-border rounded-lg">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent mt-0.5"></div>
          <div className="flex-1">
            <p className="text-sm font-medium text-foreground">Validating address...</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Checking if this address exists
            </p>
          </div>
        </div>
      )}
      
      {/* Invalid address error */}
      {validationStatus === 'invalid' && touched && value && !isValidating && (
        <div className="mt-2 flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
          <Icon name="AlertCircle" size={18} className="text-destructive flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-destructive">Invalid or incomplete address</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Please select a valid address from the dropdown suggestions as you type
            </p>
          </div>
        </div>
      )}
      
      {/* Empty field error */}
      {touched && !value && required && (
        <div className="mt-2 flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
          <Icon name="AlertCircle" size={18} className="text-destructive flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-destructive">Location required</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Please enter a starting location for this participant
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;